<script lang="ts">
  import { filters } from '../lib/stores/filters';
  import { stats } from '../lib/stores/stats';
  import { brands } from '../lib/stores/brands';
  import type { ExcludeReason, ShowMode } from '../lib/types';

  const TIERS = [
    { n: 1, label: 'Chắc chắn' },
    { n: 2, label: 'Có thể' },
    { n: 3, label: 'Yếu' },
  ] as const;

  const EXCLUDES: { reason: ExcludeReason; label: string; note: string }[] = [
    {
      reason: 'smart',
      label: 'đồng hồ thông minh',
      note: 'Apple · Fitbit · Garmin · Galaxy Watch · Amazfit · Huawei · Pixel Watch',
    },
    {
      reason: 'casio_cheap',
      label: 'Casio giá rẻ (< $120)',
      note: 'Giữ lại: MR-G · MT-G · Oceanus · Casiotron · mọi Casio > $120',
    },
    {
      reason: 'accessory',
      label: 'dây · hộp · winder · pin rời · dụng cụ',
      note: 'Đồng hồ quartz KHÔNG bị loại — chỉ loại pin rời bán riêng',
    },
  ];

  const SHOW: { value: ShowMode; label: string }[] = [
    { value: 'unseen', label: 'Chưa xem' },
    { value: 'all', label: 'Tất cả' },
    { value: 'saved', label: 'Đã lưu' },
  ];

  let brandQuery = $state('');

  const tierCount = (n: number): number => $stats?.counts_by_tier[String(n)] ?? 0;
  const excludeCount = (reason: ExcludeReason): number => $stats?.counts_by_exclude[reason] ?? 0;

  const brandSuggestions = $derived.by(() => {
    const q = brandQuery.trim().toLowerCase();
    if (q.length < 2) return [];
    return $brands
      .filter(
        (b) =>
          b.active &&
          !$filters.brands.includes(b.canonical_name.toLowerCase()) &&
          b.canonical_name.toLowerCase().includes(q),
      )
      .slice(0, 8);
  });

  function toggleTier(n: number): void {
    filters.update((f) => {
      const has = f.tiers.includes(n);
      if (has && f.tiers.length === 1) return f; // never empty
      return {
        ...f,
        tiers: has ? f.tiers.filter((t) => t !== n) : [...f.tiers, n].sort((a, b) => a - b),
      };
    });
  }

  function togglePlatform(p: string): void {
    filters.update((f) => {
      const has = f.platforms.includes(p);
      if (has && f.platforms.length === 1) return f; // never empty
      return {
        ...f,
        platforms: has ? f.platforms.filter((x) => x !== p) : [...f.platforms, p],
      };
    });
  }

  function setPrice(key: 'priceMin' | 'priceMax', raw: string): void {
    const n = raw.trim() === '' ? null : Number(raw);
    filters.update((f) => ({ ...f, [key]: n !== null && Number.isFinite(n) && n >= 0 ? n : null }));
  }

  function setShow(value: ShowMode): void {
    filters.update((f) => ({ ...f, show: value }));
  }

  function toggleExclude(reason: ExcludeReason, checked: boolean): void {
    // checked = the group is hidden (default). Unchecking asks to SEE it => add to excludeVisible.
    filters.update((f) => ({
      ...f,
      excludeVisible: checked
        ? f.excludeVisible.filter((r) => r !== reason)
        : [...f.excludeVisible, reason],
    }));
  }

  function addBrand(canonicalName: string): void {
    filters.update((f) => ({ ...f, brands: [...f.brands, canonicalName.toLowerCase()] }));
    brandQuery = '';
  }

  function removeBrand(canonical: string): void {
    filters.update((f) => ({ ...f, brands: f.brands.filter((b) => b !== canonical) }));
  }
</script>

<aside class="rail">
  <div class="grp">
    <h3>Sàn</h3>
    <label class="opt"><input type="checkbox" checked={$filters.platforms.includes('ebay')} onchange={() => togglePlatform('ebay')} /> eBay US <span class="tag">API</span></label>
    <label class="opt"><input type="checkbox" checked={$filters.platforms.includes('mercari')} onchange={() => togglePlatform('mercari')} /> Mercari <span class="tag">API</span></label>
    <label class="opt"><input type="checkbox" checked={$filters.platforms.includes('poshmark')} onchange={() => togglePlatform('poshmark')} /> Poshmark <span class="tag">API</span></label>
  </div>

  <div class="grp">
    <h3>Giá (USD)</h3>
    <div class="range">
      <input
        class="mono"
        type="number"
        min="0"
        aria-label="giá tối thiểu"
        value={$filters.priceMin ?? ''}
        onchange={(e) => setPrice('priceMin', e.currentTarget.value)}
      />
      <span>–</span>
      <input
        class="mono"
        type="number"
        min="0"
        aria-label="giá tối đa"
        value={$filters.priceMax ?? ''}
        onchange={(e) => setPrice('priceMax', e.currentTarget.value)}
      />
    </div>
  </div>

  <div class="grp">
    <h3>Tầng tin cậy</h3>
    {#each TIERS as t (t.n)}
      <label class="opt">
        <input type="checkbox" checked={$filters.tiers.includes(t.n)} onchange={() => toggleTier(t.n)} />
        {t.label}
        <span class="tag">{tierCount(t.n)}</span>
      </label>
    {/each}
  </div>

  <div class="grp">
    <h3>Hãng</h3>
    <input class="brandbox" type="text" placeholder="gõ tên hãng để lọc…" bind:value={brandQuery} />
    <div class="chips">
      {#each $filters.brands as canonical (canonical)}
        <span class="chip">
          {canonical}
          <button type="button" aria-label="bỏ {canonical}" onclick={() => removeBrand(canonical)}>✕</button>
        </span>
      {/each}
      {#each brandSuggestions as b (b.id)}
        <button type="button" class="chip suggest" onclick={() => addBrand(b.canonical_name)}>
          + {b.canonical_name}
        </button>
      {/each}
    </div>
  </div>

  <div class="grp">
    <h3>Hiển thị</h3>
    {#each SHOW as s (s.value)}
      <label class="opt">
        <input
          type="radio"
          name="show"
          value={s.value}
          checked={$filters.show === s.value}
          onchange={() => setShow(s.value)}
        />
        {s.label}
      </label>
    {/each}
  </div>

  <div class="grp">
    <h3>Loại trừ · mặc định bật</h3>
    {#each EXCLUDES as ex (ex.reason)}
      <label class="opt">
        <input
          type="checkbox"
          checked={!$filters.excludeVisible.includes(ex.reason)}
          onchange={(e) => toggleExclude(ex.reason, e.currentTarget.checked)}
        />
        {ex.label}
        <span class="tag">{excludeCount(ex.reason)}</span>
      </label>
      <div class="exnote">{ex.note}</div>
    {/each}
    <div class="swatch">
      <i style="background:var(--ok)"></i> eBay
      <i style="background:var(--tier2)"></i> tầng "có thể"
      <span style="color:var(--saved)">★</span> đã lưu
    </div>
  </div>
</aside>
